import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  ScanLine,
  Camera as CameraIcon,
  Upload as UploadIcon,
  Loader2,
} from "lucide-react";

// -----------------------------------------------------------------------------
// Capture step: live camera (preferred) with file upload fallback
// Extracted from ReceptionTab.jsx (Iteration 62 — modularisation).
// -----------------------------------------------------------------------------
export default function CaptureStep({ IT, onCapture, scanMode = "ocr" }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [mode, setMode] = useState("camera"); // source: camera | scanner | upload
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [autoBusy, setAutoBusy] = useState(false);
  const fileRef = useRef(null);
  const isMRZ = scanMode === "mrz";

  useEffect(() => {
    if (mode !== "camera") return;
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setReady(true);
      } catch (e) {
        setError(e?.message || "camera denied");
        setMode("upload");
      }
    })();
    return () => {
      cancelled = true;
      if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    };
  }, [mode]);

  const grabFrame = () => {
    const v = videoRef.current;
    if (!v) return null;
    const w = v.videoWidth, h = v.videoHeight;
    if (!w || !h) return null;
    const maxSide = 1600;
    const scale = Math.min(1, maxSide / Math.max(w, h));
    const cw = Math.round(w * scale), ch = Math.round(h * scale);
    const cv = document.createElement("canvas");
    cv.width = cw; cv.height = ch;
    cv.getContext("2d").drawImage(v, 0, 0, cw, ch);
    return cv.toDataURL("image/jpeg", 0.85);
  };

  // Manual snap for OCR mode (whole document).
  const snap = () => {
    const b64 = grabFrame();
    if (b64) onCapture(b64);
  };

  // MRZ auto-scan: sample the live video every ~1.6s and send to the backend
  // which decides via ICAO 9303 whether an MRZ was actually decoded. First
  // successful capture stops the loop (onCapture triggers step change).
  useEffect(() => {
    if (!isMRZ || mode !== "camera" || !ready) return;
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      if (cancelled) return;
      if (autoBusy) { timer = setTimeout(tick, 1600); return; }
      const b64 = grabFrame();
      if (b64) {
        setAutoBusy(true);
        try { await onCapture(b64); } finally { if (!cancelled) setAutoBusy(false); }
      }
      if (!cancelled) timer = setTimeout(tick, 1800);
    };
    timer = setTimeout(tick, 1200);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMRZ, mode, ready]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(jpeg|png|webp)$/.test(f.type)) {
      toast.error(IT ? "Formato non supportato (usa JPEG, PNG o WEBP)" : "Unsupported format (use JPEG, PNG or WEBP)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onCapture(reader.result);
    reader.readAsDataURL(f);
  };

  return (
    <div className="space-y-3">
      {/* Shared file input — mounted ONCE at the root so BOTH the "scanner"
          and "upload" drop-zones below can trigger the OS file picker.
          Before this refactor the input lived inside the `scanner` branch and
          `fileRef.current` was null when the host was in `upload` mode, which
          made the "Carica file" tile a no-op. */}
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={onFile}
        className="hidden"
        data-testid="scan-file-input"
      />

      <div className="flex gap-2 flex-wrap">
        <Button
          variant={mode === "camera" ? "default" : "outline"} size="sm"
          onClick={() => setMode("camera")} data-testid="scan-mode-camera"
          className="gap-1.5"
        >
          <CameraIcon className="w-4 h-4"/> {IT ? "Fotocamera" : "Camera"}
        </Button>
        <Button
          variant={mode === "scanner" ? "default" : "outline"} size="sm"
          onClick={() => setMode("scanner")} data-testid="scan-mode-scanner"
          className="gap-1.5"
        >
          <ScanLine className="w-4 h-4"/> {IT ? "Scanner" : "Scanner"}
        </Button>
        <Button
          variant={mode === "upload" ? "default" : "outline"} size="sm"
          onClick={() => setMode("upload")} data-testid="scan-mode-upload"
          className="gap-1.5"
        >
          <UploadIcon className="w-4 h-4"/> {IT ? "Carica file" : "Upload"}
        </Button>
      </div>

      {mode === "camera" ? (
        <div className="space-y-2">
          <div className="relative rounded-md overflow-hidden bg-ink aspect-[4/3] flex items-center justify-center">
            <video ref={videoRef} playsInline muted className="w-full h-full object-cover" data-testid="scan-video"/>
            {/* Mode-specific framing mask. MRZ mode: narrow band at the bottom
                mimicking the OCR-B lines. OCR mode: full-document rectangle. */}
            {isMRZ ? (
              <>
                <div className="absolute inset-x-6 bottom-[18%] h-[22%] border-2 border-sage/90 rounded pointer-events-none shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]"/>
                <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-sage text-white text-[10px] uppercase tracking-widest px-2 py-1 rounded-full flex items-center gap-1.5">
                  <ScanLine className="w-3 h-3 animate-pulse"/>
                  {autoBusy ? (IT ? "Analisi in corso…" : "Scanning…") : (IT ? "Inquadra la zona MRZ" : "Frame the MRZ zone")}
                </div>
              </>
            ) : (
              <>
                <div className="absolute inset-6 border-2 border-dashed border-white/60 rounded pointer-events-none shadow-[0_0_0_9999px_rgba(0,0,0,0.15)]"/>
                <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-primary text-white text-[10px] uppercase tracking-widest px-2 py-1 rounded-full flex items-center gap-1.5">
                  <CameraIcon className="w-3 h-3"/>
                  {IT ? "Inquadra tutto il documento" : "Frame the whole document"}
                </div>
              </>
            )}
            {!ready && !error && (
              <div className="absolute inset-0 grid place-items-center text-white text-sm">
                <Loader2 className="w-6 h-6 animate-spin"/>
              </div>
            )}
          </div>
          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive space-y-1" data-testid="scan-camera-error">
              <div className="font-semibold flex items-center gap-1.5">
                <CameraIcon className="w-3.5 h-3.5"/>
                {IT ? "Fotocamera non disponibile" : "Camera unavailable"}
              </div>
              <div className="opacity-90 break-words">{error}</div>
              <div className="text-muted-foreground pt-1 leading-relaxed">
                {IT
                  ? "Verifica di aver dato il permesso al browser (icona lucchetto nella barra indirizzi → Impostazioni sito → Fotocamera → Consenti). Se il problema persiste, usa \"Carica file\" o \"Scanner\"."
                  : "Check that you granted browser permission (padlock icon in the address bar → Site settings → Camera → Allow). If the issue persists, use \"Upload\" or \"Scanner\"."}
              </div>
              <div className="pt-1 flex flex-wrap gap-1.5">
                <Button size="sm" variant="outline" onClick={() => { setError(null); setMode("camera"); }} data-testid="scan-camera-retry">
                  {IT ? "Riprova" : "Retry"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setMode("upload")} data-testid="scan-camera-fallback-upload">
                  <UploadIcon className="w-3 h-3 mr-1"/>{IT ? "Passa a Carica file" : "Switch to Upload"}
                </Button>
              </div>
            </div>
          )}
          <div className="text-xs text-muted-foreground">
            {isMRZ
              ? (IT
                  ? "Modalità MRZ: la fotocamera scansiona in continuo. Tieni ferma la parte bassa del passaporto o della CIE nel riquadro verde — l'estrazione parte in automatico."
                  : "MRZ mode: the camera scans continuously. Hold the bottom of the passport or CIE inside the green frame — extraction starts automatically.")
              : (IT
                  ? "Inquadra il documento nel riquadro tratteggiato. Buona luce e senza riflessi migliorano l'estrazione."
                  : "Frame the document inside the dashed area. Good light and no glare improves extraction.")}
          </div>
          {!isMRZ && (
            <div className="flex justify-end">
              <Button onClick={snap} disabled={!ready} className="bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid="scan-capture-btn">
                <CameraIcon className="w-4 h-4 mr-1"/> {IT ? "Scatta e analizza" : "Snap & analyze"}
              </Button>
            </div>
          )}
        </div>
      ) : mode === "scanner" ? (
        <div className="space-y-2">
          {/* Scanner mode — the browser has no native TWAIN/WIA API, so we
              point the host to the OS file picker configured against the
              scanner output folder. */}
          <div
            onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-primary/40 rounded-md p-10 text-center cursor-pointer hover:border-primary/70 transition-colors bg-primary/5"
            data-testid="scan-scanner-dropzone"
          >
            <ScanLine className="w-8 h-8 mx-auto text-primary mb-2"/>
            <div className="text-sm font-medium">
              {IT ? "Importa dallo scanner collegato" : "Import from connected scanner"}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {IT
                ? "Apri lo scanner sul computer, salva il documento come JPEG/PNG, poi cliccalo qui"
                : "Open your scanner on the computer, save the document as JPEG/PNG, then click here"}
            </div>
          </div>
        </div>
      ) : (
          <div
            onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-border rounded-md p-10 text-center cursor-pointer hover:border-primary/60 transition-colors bg-sand/30"
            data-testid="scan-upload-dropzone"
          >
            <UploadIcon className="w-8 h-8 mx-auto text-muted-foreground mb-2"/>
            <div className="text-sm">
              {IT ? "Clicca per selezionare un'immagine" : "Click to select an image"}
            </div>
            <div className="text-xs text-muted-foreground">JPEG · PNG · WEBP</div>
          </div>
      )}
    </div>
  );
}
